/**
 # Scaling

## A3

A3 is 297mm wide and 420mm high.
A3 in pixels is 1130x1600 in the app currently.

1:1000 -> 0.297m*1000 = 297m width
1:1000 -> 0.420m*1000 = 420m heigth

1:800 -> 0.297m*800 = 237.6m width
1:800 -> 0.420m*800 = 336m height

1:500 -> 0.297m*500 = 148,5m width
1:500 -> 0.420m*500 = 210m heigth

1:400 -> 0.297m*400 = 118,8m width
1:400 -> 0.420m*400 = 168m heigth

1:200 -> 0.297*200 = 59,4m width
1:200 -> 0.420*200 = 84m heigth

1:100 -> 0.297*100 = 29,7m width
1:100 -> 0.420*100 = 42m heigth

1:50 -> 0.297*50 = 14.85m width
1:50 -> 0.420*50 = 21m heigth

## Pixels per meter

1:100
width: 29,7/1131.45 = 0,02625 meter per pixel
heigth: 42/1600 = 0,02625 meter per pixel

1:200
width: 59,4/1131.45 = 0,0525 meter per pixel
heigth: 84/1600 = 0,0525 meter per pixel

1:400
width: 118.8/1131.45 = 0,104 meter per pixel
heigth: 168/1600 = 0,104 meter per pixel

1:500
width: 148.5/1130 = 0,131 meter per pixel
heigth: 210/1600 = 0,131 meter per pixel

1:1000
width: 297/1131.45 = 0,2625 meter per pixel
heigth: 420/1600 = 0,2625 meter per pixel

1:800
width: 237.6/1131.45 = 0,21 meter per pixel
height: 336/1600 = 0,21 meter per pixel

1:50
width: 14.85/1131.45 = 0,013125 meter per pixel
heigth: 21/1600 = 0,013125 meter per pixel
*/

import {
  Canvas,
  getPaperPixelDims,
  getSheetName,
  PageFormat,
  SHEET_SIZES,
} from "../index";

// A '1:N' ratio string. Values come from the DB as a plain string (metric
// ladder entries below, or an arbitrary imperial ratio from
// IMPERIAL_SCALE_OPTIONS, or a legacy value carried over when a project
// switches unit system).
export type Scale = string;

// Meters-per-pixel for the legacy A3/A4 scale options. These are NOT exactly
// what `sheetLongEdgeMm / 1600 * ratio / 1000` would produce for A4 (off by
// up to ~1.5%) — kept verbatim on purpose so existing projects don't have
// their printed dimension labels shift.
const LEGACY_A3_FACTORS: Record<string, number> = {
  "1:1000": 0.2625,
  "1:800": 0.21,
  "1:500": 0.131,
  "1:400": 0.104,
  "1:250": 0.0655,
  "1:200": 0.0525,
  "1:100": 0.02625,
  "1:50": 0.013125,
};

const LEGACY_A4_FACTORS: Record<string, number> = {
  "1:1000": 0.18675,
  "1:800": 0.14925,
  "1:500": 0.093375,
  "1:400": 0.0747,
  "1:250": 0.0466875,
  "1:200": 0.03735,
  "1:100": 0.018275,
  "1:50": 0.0091375,
};

const getLegacyFactorTable = (pageFormat: PageFormat) => {
  switch (pageFormat) {
    case PageFormat.A3_LANDSCAPE:
    case PageFormat.A3_PORTRAIT:
      return LEGACY_A3_FACTORS;
    case PageFormat.A4_LANDSCAPE:
    case PageFormat.A4_PORTRAIT:
      return LEGACY_A4_FACTORS;
    default:
      return undefined;
  }
};

const SCALE_REGEX = /^1:(\d+(?:\.\d+)?)$/;

export const parseScale = (
  scale: string | null | undefined,
): { denominator: number } | null => {
  if (typeof scale !== "string") return null;
  const ratioMatch = SCALE_REGEX.exec(scale);
  if (!ratioMatch) return null;
  const denominator = Number(ratioMatch[1]);
  if (!Number.isFinite(denominator) || denominator <= 0) return null;
  return { denominator };
};

export const getScalingFactor = (
  scale = "1:200",
  pageFormat = PageFormat.A3_LANDSCAPE,
) => {
  const legacyTable = getLegacyFactorTable(pageFormat);
  if (legacyTable && scale in legacyTable) {
    return legacyTable[scale];
  }

  const ratioMatch = SCALE_REGEX.exec(scale);
  if (!ratioMatch) {
    console.warn("Invalid scale: ", scale);
    throw new Error("Invalid scale");
  }
  const ratio = Number(ratioMatch[1]);
  const { longEdgeMm } = SHEET_SIZES[getSheetName(pageFormat)];
  return (longEdgeMm / 1600) * (ratio / 1000);
};

export const getCanvasWidth = (pageFormat = PageFormat.A3_LANDSCAPE) =>
  getPaperPixelDims(pageFormat).width;

export const getCanvasHeight = (pageFormat = PageFormat.A3_LANDSCAPE) =>
  getPaperPixelDims(pageFormat).height;

export type ImperialScaleGroup = "engineering" | "architectural";

export const IMPERIAL_SCALE_OPTIONS: {
  ratio: Scale;
  label: string;
  group: ImperialScaleGroup;
}[] = [
  { ratio: "1:1200", label: "1\" = 100'", group: "engineering" },
  { ratio: "1:600", label: "1\" = 50'", group: "engineering" },
  { ratio: "1:480", label: "1\" = 40'", group: "engineering" },
  { ratio: "1:360", label: "1\" = 30'", group: "engineering" },
  { ratio: "1:240", label: "1\" = 20'", group: "engineering" },
  { ratio: "1:120", label: "1\" = 10'", group: "engineering" },
  { ratio: "1:192", label: '1/16" = 1\'-0"', group: "architectural" },
  { ratio: "1:96", label: '1/8" = 1\'-0"', group: "architectural" },
  { ratio: "1:48", label: '1/4" = 1\'-0"', group: "architectural" },
  { ratio: "1:24", label: '1/2" = 1\'-0"', group: "architectural" },
];

export const getImperialScaleLabel = (ratio: Scale): string | undefined =>
  IMPERIAL_SCALE_OPTIONS.find((option) => option.ratio === ratio)?.label;

type ScaledCanvas = Pick<Canvas, "drawingHeadScale" | "pageFormat">;

const getMetresPerPixel = (canvas: ScaledCanvas): number | null => {
  if (!parseScale(canvas.drawingHeadScale)) return null;
  if (!Object.values(PageFormat).includes(canvas.pageFormat)) return null;
  return getScalingFactor(canvas.drawingHeadScale, canvas.pageFormat);
};

export const pxToMetres = (px: number, canvas: ScaledCanvas): number | null => {
  const metresPerPixel = getMetresPerPixel(canvas);
  return metresPerPixel === null ? null : px * metresPerPixel;
};

export const metresToPx = (
  metres: number,
  canvas: ScaledCanvas,
): number | null => {
  const metresPerPixel = getMetresPerPixel(canvas);
  return metresPerPixel === null ? null : metres / metresPerPixel;
};
